import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import styled from 'styled-components'
import { DashboardInput } from 'bd-web-components'
import { SearchWrap, SearchSpinner } from './styles'
import { useClickOutside } from '../../hooks/use-click-outside'
import { APIDecoratorWithBaseURI } from '../../Service/api'

export interface PickedProducer {
  id: number
  nome: string
  eventos?: number
}

interface Props {
  value?: PickedProducer | null
  onChange: (p: PickedProducer | null) => void
  /** cor do pin (série): base = vermelho, comparado = preto */
  color?: string
}

const Wrap = styled.div`
  position: relative;
`
const Selected = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #eaecf0;
  background: #F9F9F9;
  border-radius: 11px;
  padding: 9px 12px;
  cursor: pointer;
  text-align: left;
  &:hover {
    border-color: #B0B0B0;
  }
`
const Icon = styled.span<{ $color: string }>`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #F9F9F9;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`
const SelName = styled.div`
  min-width: 0;
  b {
    display: block;
    font-weight: 600;
    font-size: 13.5px;
    color: #212121;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  span {
    font-size: 11.5px;
    color: #B0B0B0;
  }
`
const Chevron = styled.span`
  margin-left: auto;
  color: #B0B0B0;
`
const Panel = styled.div`
  position: absolute;
  z-index: 41;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 12px 32px -12px rgba(16, 24, 40, 0.24);
  padding: 10px;
`
const List = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
`
const Row = styled.li<{ $muted?: boolean }>`
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  b {
    display: block;
    font-size: 13px;
    color: ${(p) => (p.$muted ? '#B0B0B0' : '#212121')};
    font-weight: ${(p) => (p.$muted ? 500 : 560)};
  }
  span {
    font-size: 11.5px;
    color: #B0B0B0;
  }
  &:hover {
    background: #F9F9F9;
  }
`
const Hint = styled.div`
  font-size: 12px;
  color: #B0B0B0;
  padding: 10px;
  text-align: center;
`

/** Filtro de produtor (cliente) — restringe a busca de eventos do picker ao lado. OP-3414. */
const ProducerPicker: FC<Props> = ({ value, onChange, color = '#B0020B' }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  useClickOutside(rootRef, () => setOpen(false), open)
  const [term, setTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')

  // debounce da busca (300ms) — evita 1 request por tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term), 300)
    return () => clearTimeout(t)
  }, [term])

  // busca por nome no servidor (logins admin veem milhares de produtores).
  const url = open ? `producer/analytics/producers?name=${encodeURIComponent(debouncedTerm)}` : null
  const { data, isLoading, isValidating } = useSWR<any>(url, (u: string) => APIDecoratorWithBaseURI().get(u))

  const producers: PickedProducer[] = useMemo(() => {
    const arr = data?.data?.body ?? data?.data?.data ?? []
    return Array.isArray(arr) ? arr : []
  }, [data])
  const capped = producers.length >= 50

  const pick = (p: PickedProducer | null) => {
    onChange(p)
    setOpen(false)
    setTerm('')
  }

  return (
    <Wrap ref={rootRef}>
      <Selected type="button" onClick={() => setOpen((o) => !o)}>
        <Icon $color={color}>◆</Icon>
        <SelName>
          {value ? (
            <>
              <b>{value.nome}</b>
              <span>{value.eventos != null ? `${value.eventos} eventos` : 'produtor'}</span>
            </>
          ) : (
            <>
              <b style={{ color: '#B0B0B0', fontWeight: 500 }}>Todos os produtores</b>
              <span>clique para filtrar por produtor</span>
            </>
          )}
        </SelName>
        <Chevron>⌄</Chevron>
      </Selected>

      {open && (
        <Panel>
          <SearchWrap>
            <DashboardInput
              type={'input'}
              name={'search-producer'}
              placeholder={'Buscar produtor pelo nome'}
              value={term}
              onChange={(e: any) => setTerm(e.target.value)}
            />
            {isValidating && <SearchSpinner />}
          </SearchWrap>
          <List>
            <Row $muted onClick={() => pick(null)}>
              <b>Todos os produtores</b>
              <span>sem filtro de produtor</span>
            </Row>
            {isLoading ? (
              <Hint>Buscando…</Hint>
            ) : producers.length === 0 ? (
              <Hint>Nenhum produtor encontrado.</Hint>
            ) : (
              producers.map((p) => (
                <Row key={p.id} onClick={() => pick(p)}>
                  <b>{p.nome}</b>
                  <span>{p.eventos} eventos</span>
                </Row>
              ))
            )}
          </List>
          {capped && <Hint>Muitos produtores — refine a busca pelo nome.</Hint>}
        </Panel>
      )}
    </Wrap>
  )
}

export default ProducerPicker
